import { createContext, useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import type { JSONSchema7 } from "json-schema"
import { isValid } from "resha"
import { DocumentExtraction } from "universal-schema/lib/1.0.0/document-schema"

import { changeInputValue } from "../../../../Actions/Client/InputRender/InputRenderActions"
import { APIAppTemplateSchemaModel } from "../../../../Models/API/AppTemplateModel"
import {
    FetchState,
    getValue,
    hasValue
} from "../../../../Models/Client/FetchState"
import { DataFieldType } from "../../../../Models/DataProgram/DocumentExtraction"
import { APIJobModel } from "../../../../Models/API/JobModel"
import { JobSubmitDataRaw } from "../../../../Models/Client/JobSubmitData"
import { useAppState } from "../../../../Hooks/useReduxState"
import { useOnboardingAppSchema } from "../../../../Hooks/useAppSchema"

export type AnnotationsOutputList = Omit<
    DocumentExtraction["schema_instance"],
    "totalPages"
>

export interface DocumentsExtractionContext {
    appSchema: FetchState<APIAppTemplateSchemaModel>
    /**
     * Job output like annotations list
     */
    annotationsOutputList: AnnotationsOutputList | undefined
    selectedJob: APIJobModel | undefined
    setAnnotationsOutputList: (instance: AnnotationsOutputList) => void
    getAppSchema: (params: any) => void
    findOutputId: (fieldId: string) => string | undefined
    setJob: (job: APIJobModel) => void
}

export const DocumentsExtractionContext =
    createContext<DocumentsExtractionContext>({
        appSchema: undefined,
        annotationsOutputList: undefined,
        selectedJob: undefined,
        setAnnotationsOutputList: () => null,
        getAppSchema: () => null,
        findOutputId: () => undefined,
        setJob: () => null
    })

export const useDocumentsExtractionState = (
    appID: string | undefined,
    paramsSchema: JSONSchema7 | undefined,
    formData: any,
    isVisualEditor: boolean
): DocumentsExtractionContext => {
    const dispatch = useDispatch()

    const [appSchema, requestAppSchema] = useOnboardingAppSchema(appID)

    const onboarding = useAppState(
        state => state.api.interface.templateInstantiate
    )

    const entries = useAppState(state => state.client.inputRender.entries)

    const [annotationsOutputList, setAnnotationsOutputList] =
        useState<AnnotationsOutputList>()

    const [selectedJob, setJob] = useState<APIJobModel>()

    useEffect(() => {
        if (
            isVisualEditor &&
            paramsSchema !== undefined &&
            isValid(paramsSchema, formData, paramsSchema)
        ) {
            requestAppSchema({
                params: formData
            })
        }
    }, [isVisualEditor, paramsSchema, formData, requestAppSchema])

    const handleSetAnnotations = useCallback(
        (list: AnnotationsOutputList) => {
            if (onboarding !== null) {
                const values = entries[onboarding.inputDataRenderId]
                    ?.values as JobSubmitDataRaw
                if (isVisualEditor && values) {
                    dispatch(
                        changeInputValue(onboarding.inputDataRenderId, {
                            values: {
                                ...values,
                                outputs: [list]
                            },
                            surviveReroute: true,
                            errors: []
                        })
                    )
                }
            }

            setAnnotationsOutputList(list)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isVisualEditor, dispatch, changeInputValue]
    )

    const getAppSchema = useCallback(
        (additionalFields: DataFieldType[]) => {
            const additionalFieldsIds = additionalFields.map(
                ({ fieldId }) => fieldId.data
            )
            requestAppSchema({
                params: {
                    ...formData,
                    documents: [
                        {
                            ...formData.documents[0],
                            annotationFields: [
                                ...formData.documents[0].annotationFields.filter(
                                    ({ fieldId }: DataFieldType) =>
                                        !additionalFieldsIds.includes(
                                            fieldId.data
                                        )
                                ),
                                ...additionalFields
                            ]
                        }
                    ]
                }
            })
        },
        [formData, requestAppSchema]
    )

    const findOutputId = (fieldId: string) => {
        const schema: JSONSchema7 | undefined = hasValue(appSchema)
            ? (
                  getValue(appSchema)?.outputSchema?.definitions
                      ?.AnnotationModel as JSONSchema7
              )?.properties
            : undefined

        if (schema) {
            const fieldSchema = Object.entries(schema).find(
                ([, { field_id }]) => field_id === fieldId
            )
            if (fieldSchema) {
                const [outputId] = fieldSchema
                return outputId
            }
        }
        return undefined
    }

    return {
        appSchema,
        annotationsOutputList,
        selectedJob,
        setAnnotationsOutputList: handleSetAnnotations,
        getAppSchema,
        findOutputId,
        setJob
    }
}
